import { WebStorageStateStore } from 'oidc-client-ts';
import type { AuthProviderProps } from 'react-oidc-context';
import config from '../config';

const REGION = 'eu-west-2';

const origin = () => (typeof window !== 'undefined' ? window.location.origin : '');

/**
 * OIDC settings for the shared GRID Cognito user pool. Cognito publishes the
 * discovery document under the pool's issuer; the authorization endpoint it
 * advertises is the pool's hosted UI, which federates straight to COLA because
 * we ask for the `cola` identity provider. Sign-in is the authorization-code
 * flow with PKCE (public client, no secret).
 */
export const oidcConfig: AuthProviderProps = {
  authority: `https://cognito-idp.${REGION}.amazonaws.com/${config.auth.userPoolId}`,
  client_id: config.auth.clientId,
  redirect_uri: `${origin()}/auth/callback`,
  post_logout_redirect_uri: `${origin()}/auth/signed-out`,
  response_type: 'code',
  scope: 'openid email profile',
  extraQueryParams: { identity_provider: 'cola' },
  automaticSilentRenew: true,
  ...(typeof window !== 'undefined'
    ? { userStore: new WebStorageStateStore({ store: window.localStorage }) }
    : {}),
};

/** Cognito omits end_session_endpoint from its discovery document, so sign-out goes to the hosted UI logout. */
export const buildSignOutUrl = (): string => {
  const params = new URLSearchParams({
    client_id: config.auth.clientId,
    logout_uri: `${origin()}/auth/signed-out`,
  });
  return `${config.auth.hostedDomain.replace(/\/$/, '')}/logout?${params.toString()}`;
};

/** Ends the COLA session too, otherwise the next visit signs the same person straight back in. */
export const buildColaSignOutUrl = (tenant?: string | null): string => {
  const params = new URLSearchParams({ logout: 'true' });
  if (tenant) params.set('redirect', `${origin()}/${tenant}`);
  return `${config.auth.colaServiceUrl.replace(/\/$/, '')}?${params.toString()}`;
};
